/**
 * Central × Agenda (Fase 5 da agenda): "Virar tarefa" numa pendência aberta e
 * "Tarefas deste cliente" na ficha. Só aparece para quem tem a agenda nova
 * liberada (`/api/agenda/estado`); a permissão da Central o backend confere.
 * A tarefa abre na agenda (`/minhas-pendencias?tarefa=`).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiCalendar, FiCheck } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { lerEstado, lerTarefasDoPedido, tarefaDaPendencia } from "../../services/agenda";
import { formatarDataLocal } from "../../utils/dinheiro";
import { Botao, Lista, Item, Principal, Secao, SecaoTitulo, Secundario } from "./styles";

const LINK_DA_TAREFA = (id) => `/minhas-pendencias?tarefa=${id}`;
const ROTULO_STATUS = { a_fazer: "a fazer", em_andamento: "em andamento", concluida: "concluída", cancelada: "cancelada" };

function useAgendaLiberada() {
  const { request } = useAuth();
  const [liberada, setLiberada] = useState(false);
  useEffect(() => {
    let vivo = true;
    lerEstado(request).then((r) => { if (vivo) setLiberada(Boolean(r?.liberada)); }).catch(() => null);
    return () => { vivo = false; };
  }, [request]);
  return liberada;
}

/** {pendência: tarefa aberta} das pendências na tela, para o botão saber se já virou tarefa. */
export function usePendenciasNaAgenda(ids) {
  const { request } = useAuth();
  const liberada = useAgendaLiberada();
  const [mapa, setMapa] = useState({});
  const chave = ids.join(",");
  useEffect(() => {
    if (!liberada || !chave) return undefined;
    let vivo = true;
    lerTarefasDoPedido(request, "", chave.split(","))
      .then((r) => { if (vivo) setMapa(r?.pendencias_na_agenda || {}); })
      .catch(() => null);
    return () => { vivo = false; };
  }, [request, liberada, chave]);
  const marcar = useCallback((pendenciaId, tarefaId) => setMapa((m) => ({ ...m, [pendenciaId]: tarefaId })), []);
  return { liberada, mapa, marcar };
}

export function BotaoVirarTarefa({ pendencia, tarefaId, onVirou }) {
  const { request } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [enviando, setEnviando] = useState(false);
  if (tarefaId) {
    return (
      <Botao type="button" onClick={() => navigate(LINK_DA_TAREFA(tarefaId))} title="Abrir na agenda" style={{ height: 28 }}>
        <FiCheck aria-hidden="true" /> <span>Na sua agenda</span>
      </Botao>
    );
  }
  const virar = async () => {
    setEnviando(true);
    try {
      const resposta = await tarefaDaPendencia(request, pendencia.id);
      onVirou(pendencia.id, resposta.tarefa.id);
      toast.success("Virou tarefa na sua agenda (sem dia).", {
        actionLabel: "Abrir", onAction: () => navigate(LINK_DA_TAREFA(resposta.tarefa.id)),
      });
    } catch {
      // O request já mostrou o motivo.
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Botao type="button" onClick={virar} disabled={enviando} title="Pôr esta pendência na sua agenda" style={{ height: 28 }}>
      <FiCalendar aria-hidden="true" /> <span>{enviando ? "Criando…" : "Virar tarefa"}</span>
    </Botao>
  );
}

export function TarefasDoCliente({ pedido }) {
  const { request } = useAuth();
  const navigate = useNavigate();
  const liberada = useAgendaLiberada();
  const [tarefas, setTarefas] = useState(null);
  useEffect(() => {
    if (!liberada) return undefined;
    let vivo = true;
    lerTarefasDoPedido(request, pedido).then((r) => { if (vivo) setTarefas(r?.tarefas || []); }).catch(() => null);
    return () => { vivo = false; };
  }, [request, pedido, liberada]);
  if (!liberada || !tarefas || !tarefas.length) return null;
  return (
    <Secao aria-label="Tarefas deste cliente na agenda">
      <SecaoTitulo><h2>Tarefas deste cliente</h2><span>{tarefas.length}</span></SecaoTitulo>
      <Lista>
        {tarefas.map((t) => (
          <Item key={t.id}>
            <button type="button" onClick={() => navigate(LINK_DA_TAREFA(t.id))} style={{ all: "unset", cursor: "pointer", display: "block", minWidth: 0 }}>
              <Principal style={{ textDecoration: t.status === "concluida" ? "line-through" : undefined }}>{t.titulo}</Principal>
              <Secundario>
                {[t.data ? formatarDataLocal(t.data) : "sem dia", ROTULO_STATUS[t.status] || t.status, (t.participantes || []).map((p) => p.nome).join(", ")]
                  .filter(Boolean).join(" · ")}
              </Secundario>
            </button>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}
